import React, { useCallback, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { useTheme } from "../../hooks/use-theme";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { ClientWorkersTabParamList } from "../../navigations/client/WorkersNavigation";
import { useNavigation } from "@react-navigation/native";
import { useQuery } from "@apollo/client";
import { useMissionDisplayAttributes } from "../../hooks/missions";
import { Mission, ProfileType } from "../../__generated__/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import { FIND_WORKER_MISSIONS } from "../../graphql/home";
import Loading from "../../components/Loading";
import SimplePageContent from "../../components/SimplePageContent";
import ListGroup from "../../components/ListGroup";
import VStack from "../../ui_atoms/VStack";
import EmptyListInfo from "../../ui_atoms/EmptyListInfo";
import { useDateFormatter } from "../../hooks/date";
import { buildErrorHeadband } from "../ScreenCardHeadband";

type ScreenParams = StackScreenProps<RootStackParamList>;
type Props = StackScreenProps<ClientWorkersTabParamList, "WorkerMissionList">;

const EmptyMissions: React.FC = () => {
    const i18n = useI18n();
    return (
        <VStack h={1} v={1}>
            <EmptyListInfo text={i18n.t("screens.worker_missions.no_mission")} />
        </VStack>
    );
};

const WorkerMissions: React.FC<Props> = ({ route }) => {
    const i18n = useI18n();
    const styles = useStyles();
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const { loading, error, data, refetch } = useQuery(FIND_WORKER_MISSIONS, {
        variables: { workerProfileId: route?.params?.workerId || "" },
        skip: !route?.params?.workerId,
    });
    const intDateFormatter = useDateFormatter();

    const allMissions = useMemo(
        () => data?.findWorkerMissions?.elements || [],
        [data?.findWorkerMissions?.elements],
    );

    const formatDate = useCallback(
        (d?: string | null, empty = "") => {
            if (!d) {
                return empty;
            }
            return intDateFormatter(new Date(d), {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },
        [intDateFormatter],
    );

    const onSelectMission = React.useCallback(
        (missionId: string) => {
            navigation.navigate("MissionDetails", { missionId });
        },
        [navigation],
    );

    const getMissionDisplayAttributes = useMissionDisplayAttributes();

    const items = useMemo(() => {
        return allMissions.map((mission: Mission) => {
            const title = mission.workerInfo.name;
            const avatarPath = mission.workerInfo.avatarUrl;
            const avatarEmail = mission.clientInfo.email;

            const jobTitle = mission.currentContract?.jobTitle;
            const displayAttributes = getMissionDisplayAttributes(mission, ProfileType.Client);
            const description = `${jobTitle || ""}\n\n${displayAttributes.durationInfo}`;
            return {
                value: mission.id,
                title,
                avatarPath,
                avatarEmail,
                description,
                labels: [
                    { text: displayAttributes.missionStatus, mode: displayAttributes.labelMode },
                ],
                tags: [formatDate(mission.createdAt)],
                useBigAvatarOrIcon: true,
                onPress: () => {
                    onSelectMission(mission.id);
                },
            };
        });
    }, [allMissions, formatDate, getMissionDisplayAttributes, onSelectMission]);

    let children;
    if (!data && loading) {
        children = <Loading />;
    } else {
        children = (
            <View style={{ flex: 1 }}>
                <SimplePageContent
                    refresh={refetch}
                    headband={
                        error
                            ? buildErrorHeadband(
                                  i18n.t("screens.user.missions.cannot_load_missions"),
                              )
                            : undefined
                    }
                >
                    {data && allMissions && allMissions.length === 0 && <EmptyMissions />}
                    <ListGroup
                        itemStyle={styles.listGroupItem}
                        isItemsCheckable={false}
                        items={items}
                        alignment="stretch"
                    />
                </SimplePageContent>
            </View>
        );
    }
    return <View style={styles.container}>{children}</View>;
};

const useStyles = () => {
    const { gridSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    flex: 1,
                    width: "100%",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                },

                listGroupItem: {
                    paddingBottom: gridSize,
                },
            }),
        [gridSize],
    );
};
export default WorkerMissions;
